import { useSuspenseQuery } from '@tanstack/react-query';
import qs from 'qs';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { TakerMarket } from './useTakerMarkets';

interface ClearSchedule {
  clearTime: string;
  payDate: string;
  isFinalClear: boolean;
}

export interface MarketClearSchedules {
  marketUuid: string;
  supplierDivisionUuid: string;
  makerOrganizationUuid: string;
  clearSchedules?: ClearSchedule[];
}

interface ClearSchedulesQueryOptions {
  takerMarkets?: TakerMarket[];
  startDate?: string;
  endDate?: string;
}

const fetchClearSchedules = async (options?: ClearSchedulesQueryOptions): Promise<MarketClearSchedules[]> => {
  const supplierMarketsFilter = options?.takerMarkets?.map((tm) => ({
    makerOrganizationUuid: tm.makerOrganizationUuid,
    marketUuid: tm.marketUuid,
    supplierDivisionUuid: tm.takerDivisionUuid,
  }));

  const response = await apiClient
    .get(`api/supplier-experience/supplier-markets/clear-schedules`, {
      searchParams: qs.stringify(
        {
          supplierMarkets: supplierMarketsFilter,
          ...(options?.startDate && { startDate: options.startDate }),
          ...(options?.endDate && { endDate: options.endDate }),
        },
        { arrayFormat: 'indices' }
      ),
    })
    .json<paths['/supplier-markets/clear-schedules']['get']['responses']['200']['content']['application/json']>();

  const marketClearSchedules = response.data.supplierMarketClearSchedulesGroups
    .map((smcsg) => smcsg.supplierMarketClearSchedules)
    .flatMap((smcs) => smcs);

  return marketClearSchedules;
};

const fetchClearSchedulesBatched = async (options?: ClearSchedulesQueryOptions): Promise<MarketClearSchedules[]> => {
  const { takerMarkets } = options ?? {};

  if (!takerMarkets?.length) {
    return [];
  }

  // Split takerMarkets into batches of 10
  const batchSize = 10;
  const batches: TakerMarket[][] = [];

  for (let i = 0; i < takerMarkets.length; i += batchSize) {
    batches.push(takerMarkets.slice(i, i + batchSize));
  }

  // Build array of promises
  const promises = batches.map((batch) =>
    fetchClearSchedules({
      ...options,
      takerMarkets: batch,
    })
  );

  // Run all in parallel
  try {
    const results = await Promise.all(promises);
    // Flatten all results
    return results.flat();
  } catch (error) {
    console.error('Error fetching one or more batches of clear schedules:', error);
    throw error;
  }
};

const useClearSchedules = (options?: ClearSchedulesQueryOptions) => {
  return useSuspenseQuery({
    queryKey: [
      'clear-schedules',
      options?.takerMarkets?.map((tm) => tm.marketUuid),
      options?.startDate,
      options?.endDate,
    ],
    queryFn: () => (!!options?.takerMarkets ? fetchClearSchedulesBatched(options) : []),
  });
};

export default useClearSchedules;
